/**
 * Wrapped: a photo, given a tile at a time.
 *
 * Three tabs, because there are three genuinely different jobs — making one, opening one, and
 * watching one you sent. Give is a shutter: the photo and its caption are the whole visible form,
 * and everything that was six cards of settings folds into one Terms row carrying last time's
 * answers, so the ordinary wrap is two taps and the terms are still yours to change. Received is
 * one picture being uncovered rather than a grid beside a thumbnail. Sent is a status board with
 * two irreversible buttons on it.
 *
 * Every unpeeled tile is absent from the payload rather than hidden by this file. There is no
 * route to the bytes at all, so nothing here could show one if it tried — and the covers the
 * frame draws sit over the teaser, which is the only image a recipient is given for free.
 */
import { Chip } from '@/components/chip';
import { Empty } from '@/components/empty';
import { Field } from '@/components/field';
import { Panel } from '@/components/panel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Text } from '@/components/ui/text';
import { CoveredPicture } from '@/components/covered-picture';
import { api } from '@/api';
import { dayLabel } from '@/lib/day';
import { prepareTeaser, prepareWrap, type WrapTeaser } from '@/lib/tiles';
import { cn } from '@/lib/utils';
import { statusWord, whenLabel } from '@/lib/wrapWords';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { moduleById, type ScreenId } from '@/registry';
import { usePoll } from '@/usePoll';
import { useLayout } from '@/useLayout';
import { useStore } from '@/store';
import * as ImagePicker from 'expo-image-picker';
import * as React from 'react';
import { Image, Platform, Pressable, View } from 'react-native';

interface WrapSummary {
  id: string;
  caption: string | null;
  grid: number;
  cols: number;
  priceKind: string;
  /** The server's words for that price — never re-derived here. */
  priceLabel: string;
  unlockDay: string | null;
  keep: 'vanish' | 'scrapbook';
  expiresAt: string | null;
  status: string;
  peeled: number;
  credits?: number | null;
  teaser?: string | null;
  holdUntil: string | null;
  openedAt: string | null;
  createdAt?: string;
  lastTileWaiting?: boolean;
}

interface WrapDetail extends WrapSummary {
  role: 'giver' | 'recipient';
  tiles: { idx: number; peeled: boolean; image?: string | null }[];
  /** The photograph's own proportions, which the frame and every tile in it are cut to. */
  width?: number | null;
  height?: number | null;
  full?: string | null;
}

/** What one peel answers with: the tile that was just earned, and what is left to spend. */
interface Peeled {
  status: string;
  peeled: number;
  credits: number;
  image?: string | null;
  full?: string | null;
}

/**
 * What a price is called is the server's answer, not this file's.
 *
 * There used to be a table of these labels here as well as in `wrapCredits.ts`. When the date and
 * co-op prices were scoped to the recipient — the giver could open the gift they were giving —
 * this copy went on describing the old rule, which is the failure mode a duplicated table always
 * has. `/api/wraps` serves the catalogue for the chooser, and every wrap carries its own label.
 *
 * `short` names the option on its pill and `label` is the clause a sentence can take. Both come
 * from the same server table for the same reason.
 */
interface Price {
  id: string;
  label: string;
  short: string;
}

/**
 * WHICH PANEL PAYS A PRICE.
 *
 * A locked cover that only says what it costs is a receipt. The point of the price is that the
 * thing it asks for is something the game already wanted from you, so the cover names the panel it
 * is one tap away from and takes you there. Two of the six are not their own panel — a co-op night
 * is logged on the Scoreboard and a scratched date card is Date Night's — and `day` is nobody's
 * panel at all: the giver named a date and there is nothing to do but wait for it, so that cover
 * shows the date instead of a button that would be a lie about being able to hurry it.
 *
 * The button's words are the module's OWN tab label, read out of the registry rather than written
 * again here: a second table of panel names is a table that goes on saying "Gratitude" after the
 * tab has been called Thanks for a year.
 */
const PRICE_PANEL: Record<string, ScreenId | undefined> = {
  sync: 'sync',
  gratitude: 'gratitude',
  mission: 'missions',
  date: 'dates',
  coop: 'score',
  day: undefined,
};

const EXPIRY_CHOICES = [3, 7, 14];

/** What a first wrap is given on, before there is a last one to remember. */
const DEFAULT_TERMS = {
  grid: 9,
  priceKind: 'sync',
  unlockDay: '',
  keep: 'vanish' as 'vanish' | 'scrapbook',
  expiresDays: 7,
};

const daysUntil = (iso: string | null, now = Date.now()) =>
  iso ? Math.max(0, Math.ceil((Date.parse(iso) - now) / 86_400_000)) : 0;

/**
 * How long the last wrap was given, read back out of the two instants the server records.
 *
 * Anything that is not one of the three the chips offer — a hand-made wrap, a clock that moved,
 * a row that never got an expiry — falls back to the default rather than putting a number on a
 * chip row that cannot show it. A remembered term the form cannot represent is worse than none.
 */
function expiryDaysOf(wrap: WrapSummary) {
  if (!wrap.expiresAt || !wrap.createdAt) return DEFAULT_TERMS.expiresDays;
  const days = Math.round((Date.parse(wrap.expiresAt) - Date.parse(wrap.createdAt)) / 86_400_000);
  return EXPIRY_CHOICES.includes(days) ? days : DEFAULT_TERMS.expiresDays;
}

const termsFrom = (wrap?: WrapSummary) =>
  wrap
    ? {
        grid: wrap.grid,
        priceKind: wrap.priceKind,
        unlockDay: wrap.unlockDay ?? '',
        keep: wrap.keep,
        expiresDays: expiryDaysOf(wrap),
      }
    : DEFAULT_TERMS;

export function WrappedScreen() {
  const { me, selected, select, refreshBadges, fail } = useStore();
  const [received, setReceived] = React.useState<WrapSummary[]>([]);
  const [sent, setSent] = React.useState<WrapSummary[]>([]);
  const [prices, setPrices] = React.useState<Price[]>([]);
  const [tab, setTab] = React.useState('give');
  const [openedOpen, setOpenedOpen] = React.useState(false);
  // A tab the person chose is theirs. Once they have touched one, the poll below never moves them.
  const picked = React.useRef(false);
  const choose = (value: string) => {
    picked.current = true;
    setTab(value);
  };
  const them = me?.partnerName ?? 'them';

  const load = React.useCallback(async () => {
    const data = await api.get<{ received: WrapSummary[]; sent: WrapSummary[]; prices: Price[] }>(
      '/api/wraps',
    );
    setReceived(data.received);
    setSent(data.sent);
    setPrices(data.prices ?? []);
    // Landing on Give while a gift sits one tap away under Received is the wrong door.
    if (!picked.current && data.received.some((w) => w.status !== 'opened')) setTab('received');
  }, []);

  const { loaded, error, reload } = usePoll(load, 20_000);

  const after = async () => {
    await Promise.all([load(), refreshBadges()]);
  };

  /**
   * `sel` on the panel names one received wrap, so the tab shows that one alone. Nothing is
   * hidden by it — the way back is a button, and a single wrap has nothing to go back to.
   */
  const active = received.find((w) => w.id === selected.wrapped) ?? null;

  /**
   * WAITING AND OPENED ARE TWO LISTS, AND THE TAB COUNTS ONE OF THEM.
   *
   * The server keeps an opened wrap in Received forever, which is right — a vanish wrap has a day
   * left on it and a kept one is a thing you go back and look at. What was wrong was the count:
   * "Received · 7" on a tab where nothing has been peelable for a month is a number that stops
   * meaning anything, and it disagreed with the badge, which has always counted only what you can
   * act on. So the split is here rather than in the query: the tab counts what is waiting, the
   * opened ones fold away under their own row, and no payload changed to do it.
   */
  const waiting = received.filter((w) => w.status !== 'opened');
  const opened = received.filter((w) => w.status === 'opened');

  if (!loaded) return (
    <Panel id="wrapped">
      <PanelSkeleton rows={2} />
    </Panel>
  );
  if (error) return (
    <Panel id="wrapped">
      <LoadFailed onRetry={reload} reason={error} />
    </Panel>
  );

  return (
    <Panel id="wrapped">
      <Tabs value={tab} onValueChange={choose}>
        <TabsList>
          <TabsTrigger value="give">
            <Text>Give</Text>
          </TabsTrigger>
          <TabsTrigger value="received">
            <Text>Received · {waiting.length}</Text>
          </TabsTrigger>
          <TabsTrigger value="sent">
            <Text>Sent · {sent.length}</Text>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="give">
          <GiveTab
            partnerName={them}
            prices={prices}
            lastWrap={sent[0]}
            onSealed={after}
            onFail={fail}
            onDone={() => choose('sent')}
          />
        </TabsContent>

        <TabsContent value="received">
          {received.length ? (
            active ? (
              <>
                {received.length > 1 ? (
                  <Button variant="ghost" size="sm" className="self-start" onPress={() => select('wrapped', null)}>
                    <Text>← All {received.length} wrapped</Text>
                  </Button>
                ) : null}
                <ReceivedWrap summary={active} partnerName={them} onChange={after} onFail={fail} />
              </>
            ) : (
              <>
                {waiting.length ? (
                  waiting.map((wrap) => (
                    <ReceivedWrap key={wrap.id} summary={wrap} partnerName={them} onChange={after} onFail={fail} />
                  ))
                ) : (
                  <Empty
                    mark="▨"
                    title="Nothing waiting"
                    body={`Everything ${them} wrapped for you is open. The next one lands here as a blur.`}
                  />
                )}

                {opened.length ? (
                  <Collapsible open={openedOpen} onOpenChange={setOpenedOpen} className="gap-3">
                    <CollapsibleTrigger
                      accessibilityLabel={`${opened.length} opened. ${openedOpen ? 'Collapse' : 'Show them'}`}
                      className="border-border w-auto justify-between gap-2 self-auto rounded-md border p-3 hover:bg-accent active:bg-accent">
                      <Text className="font-semibold">Opened ({opened.length})</Text>
                      <Text variant="small" className="text-muted-foreground">
                        {openedOpen ? '▴' : '▾'}
                      </Text>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="gap-3">
                      {opened.map((wrap) => (
                        <ReceivedWrap key={wrap.id} summary={wrap} partnerName={them} onChange={after} onFail={fail} />
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                ) : null}
              </>
            )
          ) : (
            <Empty
              mark="▨"
              title="Nothing wrapped for you"
              body={`When ${them} wraps something, it lands here as a blur — and you peel it a tile at a time.`}
            />
          )}
        </TabsContent>

        <TabsContent value="sent">
          {sent.length ? (
            sent.map((wrap) => (
              <SentWrap key={wrap.id} summary={wrap} partnerName={them} onChange={after} onFail={fail} />
            ))
          ) : (
            <Empty mark="▨" title="Nothing sent yet" body={`Wrap a photo for ${them} — they earn it open a tile at a time.`} />
          )}
        </TabsContent>
      </Tabs>
    </Panel>
  );
}

/**
 * Making one: the shutter, the caption, and last time's terms folded into one line.
 *
 * The terms are a decision somebody makes once and then keeps — how many tiles, what a tile
 * costs, what happens afterwards — so the form remembers the last wrap's answers and states them
 * in a sentence rather than asking all four again. The row opens in place, which is what keeps
 * them changeable without making them the screen.
 *
 * Four pick-one rows and one pick-one idiom. The price used to be a column of square boxes with a
 * filled one for the answer — a checkbox shape doing a radio's job, in a card that already had two
 * rows of pills — so it is pills too now, and the chosen price's full terms are stated once
 * underneath rather than on all six.
 */
function GiveTab({
  partnerName,
  lastWrap,
  prices,
  onSealed,
  onFail,
  onDone,
}: {
  partnerName: string;
  /** The price catalogue as the server describes it — the chooser's only source of words. */
  prices: Price[];
  /** The most recent wrap sent, which is what the terms are remembered from. */
  lastWrap?: WrapSummary;
  onSealed: () => Promise<void>;
  onFail: (e: unknown) => void;
  onDone: () => void;
}) {
  const [uri, setUri] = React.useState<string | null>(null);
  const [caption, setCaption] = React.useState('');
  const [grid, setGrid] = React.useState(DEFAULT_TERMS.grid);
  const [priceKind, setPriceKind] = React.useState(DEFAULT_TERMS.priceKind);
  const [unlockDay, setUnlockDay] = React.useState(DEFAULT_TERMS.unlockDay);
  const [keep, setKeep] = React.useState<'vanish' | 'scrapbook'>(DEFAULT_TERMS.keep);
  const [expiresDays, setExpiresDays] = React.useState(DEFAULT_TERMS.expiresDays);
  const [termsOpen, setTermsOpen] = React.useState(false);
  const [preview, setPreview] = React.useState<WrapTeaser | null>(null);
  const [progress, setProgress] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const { wide } = useLayout();

  /**
   * Seeded once per wrap, keyed on its id rather than on the object: the list is re-fetched every
   * twenty seconds, and an effect that ran on identity would overwrite a term somebody had just
   * changed. Sending re-seeds off the wrap that was just sent, which is the same answer again.
   */
  const seededFrom = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!lastWrap || seededFrom.current === lastWrap.id) return;
    seededFrom.current = lastWrap.id;
    const terms = termsFrom(lastWrap);
    setGrid(terms.grid);
    setPriceKind(terms.priceKind);
    setUnlockDay(terms.unlockDay);
    setKeep(terms.keep);
    setExpiresDays(terms.expiresDays);
  }, [lastWrap]);

  /**
   * The teaser, made here so the giver sees the thing they are actually sending.
   *
   * A wrap is the one screen in the app where the sender cannot picture what arrives: they are
   * looking at their own photograph at full size and the recipient gets a twelve-pixel smear under
   * a grid. Guessing at how much shows through is how somebody wraps a picture whose whole joke is
   * a detail the blur eats. It is the same two resizes the upload runs, off the picked file, and
   * `live` is the guard against a second photo being chosen while the first one is still blurring.
   */
  React.useEffect(() => {
    if (!uri) {
      setPreview(null);
      return;
    }
    let live = true;
    setPreview(null);
    void prepareTeaser(uri)
      .then((made) => live && setPreview(made))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [uri]);

  const cols = Math.round(Math.sqrt(grid));
  const chosen = prices.find((p) => p.id === priceKind);
  const dayValid = priceKind !== 'day' || /^\d{4}-\d{2}-\d{2}$/.test(unlockDay);
  const dayNote = priceKind === 'day' && unlockDay ? ` (${unlockDay})` : '';
  /**
   * The summary as SEGMENTS rather than one string, because at 320px one string wraps wherever it
   * likes and put a lone "·" at the start of line two. Each segment carries its own trailing
   * separator, tied on with a non-breaking space, so a break can only ever happen after a dot.
   */
  const termsParts = [
    `${grid} tiles`,
    `${chosen?.short ?? priceKind}${dayNote}`,
    keep === 'vanish' ? 'vanishes' : 'kept',
    `${expiresDays} days`,
  ];

  const pick = async () => {
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 1 }).catch(() => null);
    const asset = result && !result.canceled ? result.assets[0] : null;
    if (asset?.uri) setUri(asset.uri);
  };

  const send = async () => {
    if (!uri) return;
    setBusy(true);
    try {
      setProgress('Cutting it up on this device…');
      const images = await prepareWrap(uri, cols);

      setProgress('Wrapping…');
      const created = await api.post<{ id: string }>('/api/wraps', {
        grid,
        priceKind,
        unlockDay: priceKind === 'day' ? unlockDay : undefined,
        keep,
        expiresDays,
        caption: caption.trim() || undefined,
        teaser: images.teaser,
        width: images.width,
        height: images.height,
      });

      await api.request('PUT', `/api/wraps/${created.id}/full`, { bytes: images.full });
      for (let i = 0; i < images.tiles.length; i++) {
        setProgress(`Sending tile ${i + 1} of ${images.tiles.length}…`);
        await api.request('PUT', `/api/wraps/${created.id}/tiles/${i}`, { bytes: images.tiles[i] });
      }

      setProgress('Sealing…');
      await api.post(`/api/wraps/${created.id}/seal`, { expiresDays });

      setUri(null);
      setCaption('');
      setProgress('');
      await onSealed();
      onDone();
    } catch (e) {
      // A half-uploaded draft is swept by the tick within a day, so stopping here costs nothing.
      setProgress('That did not go through. Nothing was sent — try again.');
      onFail(e);
    } finally {
      setBusy(false);
    }
  };

  const photoCard = (
    <Card>
      <CardContent>
        {/**
         * `contain`, not `cover`. The wrap is never cropped — `prepareWrap` cuts the picture as
         * it is — so a preview that trimmed it would be showing a framing the recipient will
         * not get, which is the one thing a preview may not do.
         */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={uri ? 'Choose a different photo' : 'Choose a photo'}
          onPress={pick}
          style={{ aspectRatio: 1 }}
          className={cn(
            // Square, and capped: `w-full` is the phone, where the well is the screen and that is
            // the point. A desk column is 880px wide, and an uncapped square there is a 880px
            // skyscraper you scroll past to reach the caption — the one field that has to be
            // visible beside it. Centred rather than left, so the cap does not read as a
            // half-finished row.
            'bg-muted border-border w-full max-w-[420px] items-center justify-center self-center overflow-hidden rounded-lg border active:opacity-70',
            !uri && 'border-dashed',
          )}>
          {uri ? (
            <Image source={{ uri }} className="h-full w-full" resizeMode="contain" />
          ) : (
            <View className="items-center gap-1">
              <Text variant="display" className="text-muted-foreground">
                ▨
              </Text>
              <Text variant="small" className="text-muted-foreground">
                Choose a photo
              </Text>
            </View>
          )}
        </Pressable>

        {/* A caption is a phrase, and a phrase in a 1300px input is a word in a corridor. The
            input stops at a readable measure exactly as `CardDescription` does. */}
        <Field
          className="w-full max-w-[560px]"
          label="Caption"
          value={caption}
          onChangeText={setCaption}
          maxLength={200}
          placeholder="Something to look forward to"
          autoComplete="off"
          message={
            uri
              ? 'Tap the photo to choose another. Tiled and downscaled here before anything is uploaded.'
              : 'Tiled and downscaled on this device before anything is uploaded — only peeled tiles ever leave the server.'
          }
        />
      </CardContent>
    </Card>
  );

  const termsCard = (
    <Card>
      {/* RNR's Collapsible rather than a Pressable and a boolean: the trigger reports `expanded`
          to a screen reader on its own, and the six settings under it are ABSENT while folded
          rather than hidden — which for a form is the difference between "not shown" and "not
          there". `gap-0` because the card's two halves carry their own padding. */}
      <Collapsible open={termsOpen} onOpenChange={setTermsOpen} className="gap-0">
        <CollapsibleTrigger
          accessibilityLabel={`Terms: ${termsParts.join(', ')}. ${termsOpen ? 'Collapse' : 'Change them'}`}
          className="w-auto flex-wrap justify-between gap-2 self-auto p-4 hover:bg-accent active:bg-accent">
          <View className="min-w-0 flex-1 flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <Text className="font-semibold">{'Terms ·'}</Text>
            {termsParts.map((part, i) => (
              <Text key={i} variant="small" className="text-muted-foreground">
                {i === termsParts.length - 1 ? part : `${part} ·`}
              </Text>
            ))}
          </View>
          <Text variant="small" className="text-muted-foreground">
            {termsOpen ? '▴' : '▾'}
          </Text>
        </CollapsibleTrigger>

        <CollapsibleContent className="border-border gap-5 border-t p-4">
          <View className="gap-2">
            <Text variant="label">Tiles</Text>
            <View className="flex-row flex-wrap gap-2">
              {[4, 9, 16].map((n) => (
                <Chip
                  key={n}
                  tone="tonal"
                  label={String(n)}
                  selected={grid === n}
                  onPress={() => setGrid(n)}
                  className="w-14"
                />
              ))}
            </View>
          </View>

          <View className="gap-2">
            <Text variant="label">Price per tile</Text>
            <CardDescription>From what the game already asks for.</CardDescription>
            <View className="flex-row flex-wrap gap-2">
              {prices.map((price) => (
                <Chip
                  key={price.id}
                  tone="tonal"
                  label={price.short}
                  selected={priceKind === price.id}
                  onPress={() => setPriceKind(price.id)}
                />
              ))}
            </View>
            {/* The terms of the ONE that is chosen. Six clauses on six pills is a paragraph broken
                into buttons, and five of them are about an answer nobody gave. */}
            {chosen ? (
              <Text variant="small" className="text-muted-foreground">
                A tile costs {chosen.label}.
              </Text>
            ) : null}
            {priceKind === 'day' ? (
              <Field
                label="Opens on"
                value={unlockDay}
                onChangeText={setUnlockDay}
                placeholder="2026-12-25"
                maxLength={10}
                autoComplete="off"
                invalid={!dayValid}
                message={dayValid ? undefined : 'A date, written 2026-12-25.'}
              />
            ) : null}
            <Text variant="small" className="text-muted-foreground">
              Nothing vulnerable is ever a price: no Spark answers, no Pulse ratings, no signals.
            </Text>
          </View>

          <View className="gap-2">
            <Text variant="label">Afterwards</Text>
            <View className="flex-row flex-wrap gap-2">
              <Chip
                tone="tonal"
                label="Vanish after viewing"
                selected={keep === 'vanish'}
                onPress={() => setKeep('vanish')}
              />
              <Chip
                tone="tonal"
                label="Keep in scrapbook"
                selected={keep === 'scrapbook'}
                onPress={() => setKeep('scrapbook')}
              />
            </View>
          </View>

          <View className="gap-2">
            <Text variant="label">Expires unpeeled after</Text>
            <View className="flex-row flex-wrap gap-2">
              {EXPIRY_CHOICES.map((n) => (
                <Chip
                  key={n}
                  tone="tonal"
                  label={`${n} days`}
                  selected={expiresDays === n}
                  onPress={() => setExpiresDays(n)}
                />
              ))}
            </View>
          </View>
        </CollapsibleContent>
      </Collapsible>

      {/**
       * What sending actually does, said before the button rather than in a dialog after it, and
       * INSIDE the card whose settings it describes — it used to float between the Terms card and
       * the CTA, belonging to neither. It sits outside the disclosure rather than at the end of
       * it, because it is true of the ordinary two-tap wrap, which never opens the terms at all.
       *
       * It is all recoverable — a wrap can be withdrawn until the last tile — so the sentence is
       * information, and a confirm step here would only be a third tap.
       */}
      <Text variant="note" className="px-4 pb-4">
        {partnerName}’s push says “Wrapped” and nothing else. They get a blurred teaser and {grid} tiles to earn, and
        you can withdraw it any time before the last one.
      </Text>
    </Card>
  );

  return (
    <>
      {/**
       * Two columns once there is room, because the photo well is capped at 420px and the terms
       * are what somebody looks at while looking at the photo. Below the breakpoint they stack, and
       * `wide` is the only thing consulted — a screen that compares against a pixel has taken a
       * dependency on a decision the design file owns.
       */}
      {wide ? (
        <View className="flex-row items-start gap-4">
          <View className="min-w-0 flex-1">{photoCard}</View>
          <View className="min-w-0 flex-1">{termsCard}</View>
        </View>
      ) : (
        <>
          {photoCard}
          {termsCard}
        </>
      )}

      {uri ? (
        <Card>
          <CardContent>
            {/* One STRING child, not an interpolated pair of them: `CardHead` draws a string as
                its own heading and anything else as raw children, and raw text in a View is a
                crash on native rather than a layout bug. */}
            <CardHead>{`What ${partnerName} sees`}</CardHead>
            {preview ? (
              <View className="w-full max-w-[420px] self-center">
                <CoveredPicture
                  teaser={preview.teaser}
                  width={preview.width}
                  height={preview.height}
                  cols={cols}
                  grid={grid}
                  tiles={Array.from({ length: grid }, (_, idx) => ({ idx, peeled: false }))}
                />
              </View>
            ) : (
              <Text variant="small" className="text-muted-foreground">
                Blurring it…
              </Text>
            )}
            <Text variant="note">
              The real teaser, made the same way the upload makes it: twelve pixels wide, blown back up, under {grid}{' '}
              covers. Nothing here has left this device.
            </Text>
          </CardContent>
        </Card>
      ) : null}

      <Button
        disabled={!uri || busy || !dayValid}
        reason={!uri ? 'Choose a photo first' : !dayValid ? 'Fix the unlock date first' : undefined}
        onPress={send}>
        <Text>Wrap it for {partnerName}</Text>
      </Button>

      {progress ? (
        <Text variant="small" className="text-muted-foreground">
          {progress}
        </Text>
      ) : null}
    </>
  );
}

/** Opening one: the picture, covered, and what a cover costs. */
function ReceivedWrap({
  summary,
  partnerName,
  onChange,
  onFail,
}: {
  summary: WrapSummary;
  partnerName: string;
  onChange: () => Promise<void>;
  onFail: (e: unknown) => void;
}) {
  const { refreshBadges, go } = useStore();
  const [detail, setDetail] = React.useState<WrapDetail | null>(null);
  /** Which explanation the frame is carrying, if a locked cover has been pressed. */
  const [notice, setNotice] = React.useState<'locked' | 'held' | null>(null);
  const [busy, setBusy] = React.useState(false);
  const counted = React.useRef(false);
  const { wide } = useLayout();

  const load = React.useCallback(async () => {
    const data = await api.get<WrapDetail>(`/api/wraps/${summary.id}`).catch(() => null);
    if (!data) return;
    setDetail(data);
    // This GET is what marks the wrap seen, so the tab badge is stale the moment it lands. Correct
    // it once, here, rather than leaving the count wrong until the five-minute heartbeat.
    if (!counted.current) {
      counted.current = true;
      void refreshBadges();
    }
  }, [summary.id, refreshBadges]);

  React.useEffect(() => {
    void load();
  }, [load, summary.peeled, summary.status]);

  const wrap = detail ?? summary;
  const opened = wrap.status === 'opened';

  // A hold is the giver drawing the moment out on purpose, so the recipient has to be told --
  // silently swallowing the tap would read as a broken button. What they must NOT get is the
  // server's refusal verbatim: that is an ISO timestamp in the middle of a sentence about a
  // present. The frame says it in the words a person uses for a time, and the tap never fails.
  const held = !!wrap.holdUntil && wrap.holdUntil > new Date().toISOString();
  const heldWhen = wrap.holdUntil ? whenLabel(wrap.holdUntil) : '';

  const credits = detail?.credits ?? summary.credits ?? 0;
  const price = wrap.priceLabel;
  const canPeel = !held && credits >= 1;
  const panel = PRICE_PANEL[wrap.priceKind];
  const panelName = panel ? moduleById(panel)?.title : undefined;
  /** A tile that is earned and paid for and whose bytes did not arrive. Said, not left blank. */
  const missing = (detail?.tiles ?? []).some((t) => t.peeled && !t.image);

  /**
   * One peel, applied from the answer rather than from a second round trip.
   *
   * The old shape awaited a full refetch of the wrap before the cover could move — every peeled
   * tile re-serialised as base64 and back down the wire to reveal ONE of them — so the lift ran
   * a second or more after the tap, which on a slow connection is a cover that appears to have
   * stuck. The peel response already carries exactly what changed.
   *
   * It carries it for ONE INDEX, and that is the whole of what is applied here: the image the
   * server just returned, on the tile it was returned for. Not a merge, not a spread of a tiles
   * array — there is no unpeeled tile's bytes in this payload to leak, and there is no code path
   * here that could put an image on a tile the server did not name.
   *
   * `status` and `full` are deliberately NOT applied. The last peel opens the wrap, and taking
   * that from the response would swap the frame for the whole photograph while the final cover is
   * still fading. The refetch after the lift is what opens it, in that order.
   */
  const peel = async (idx: number) => {
    // Belt under the locked cover: credits can go to nothing between a render and a press, and
    // the server's refusal for a hold has an ISO instant in it that must not reach a person.
    if (held || credits < 1) {
      setNotice(held ? 'held' : 'locked');
      return false;
    }
    setNotice(null);
    setBusy(true);
    try {
      const result = await api.post<Peeled>(`/api/wraps/${summary.id}/peel`, { idx });
      setDetail((current) =>
        current
          ? {
              ...current,
              peeled: result.peeled,
              credits: result.credits,
              tiles: current.tiles.map((tile) =>
                tile.idx === idx ? { idx, peeled: true, image: result.image } : tile,
              ),
            }
          : current,
      );
      return true;
    } catch (e) {
      onFail(e);
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** What a locked cover answers with, drawn on the frame where the press happened. */
  const noticeNode = notice ? (
    <>
      <Text variant="small">
        {notice === 'held'
          ? `${partnerName} is holding the last one back until ${heldWhen}.`
          : wrap.priceKind === 'day' && wrap.unlockDay
            ? `This one opens on ${dayLabel(wrap.unlockDay)}.`
            : `A tile costs ${price}.`}
      </Text>
      <View className="flex-row flex-wrap items-center gap-2">
        {notice === 'locked' && panel && panelName ? (
          <Button size="sm" onPress={() => go(panel)}>
            <Text>Go to {panelName}</Text>
          </Button>
        ) : null}
        {/* `flush={false}`: a ghost's negative margin would slide its box back over the primary
            button beside it, and over the notice's own padding when it stands alone. */}
        <Button size="sm" variant="ghost" flush={false} onPress={() => setNotice(null)}>
          <Text>Not now</Text>
        </Button>
      </View>
    </>
  ) : null;

  const frame = (
    <CoveredPicture
      teaser={wrap.teaser}
      width={detail?.width}
      height={detail?.height}
      cols={wrap.cols}
      grid={wrap.grid}
      tiles={detail?.tiles ?? Array.from({ length: wrap.grid }, (_, idx) => ({ idx, peeled: false }))}
      credits={credits}
      canPeel={canPeel}
      busy={busy}
      lockedLabel={held ? `held until ${heldWhen}` : credits < 1 ? 'not earned yet' : undefined}
      notice={noticeNode}
      onPeel={peel}
      onLocked={() => setNotice(held ? 'held' : 'locked')}
      onRetry={() => void load()}
      onLifted={() => void Promise.all([load(), onChange()])}
    />
  );

  const meta = (
    <>
      <Text variant="small" className="text-muted-foreground">
        Peeled {wrap.peeled} of {wrap.grid}
      </Text>
      {/* The named-day price is the one with no act behind it, so it gets the date rather than
          the nudge: there is nothing to earn and telling somebody to go and earn one would be an
          instruction they cannot follow. */}
      <Text variant="small" className="text-muted-foreground">
        A cover costs {price}.
        {wrap.priceKind === 'day' && wrap.unlockDay
          ? ` It opens on ${dayLabel(wrap.unlockDay)}.`
          : credits < 1 && !held
            ? ' Earn one and a cover lifts.'
            : ''}
      </Text>
      {missing ? (
        <Text variant="small" className="text-muted-foreground">
          Couldn’t load this tile · tap to retry
        </Text>
      ) : null}
      {held ? (
        <Text variant="small" className="text-gold-text">
          {partnerName} is holding the last one back until {heldWhen}.
        </Text>
      ) : null}
    </>
  );

  return (
    <Card>
      <CardContent>
        <CardHead row>
          <Text variant="heading" className="min-w-0 flex-1">
            From {partnerName}
          </Text>
          {!opened ? <Text variant="small">expires in {daysUntil(wrap.expiresAt)} days</Text> : null}
        </CardHead>
        {wrap.caption ? <Text className="italic">“{wrap.caption}”</Text> : null}

        {opened && detail?.full ? (
          <>
            <Image
              source={{ uri: detail.full }}
              className="w-full rounded-lg"
              style={{ aspectRatio: detail.width && detail.height ? detail.width / detail.height : 1 }}
              resizeMode="cover"
            />
            <Text variant="small" className="text-muted-foreground">
              {wrap.keep === 'vanish' ? 'Opened — this vanishes in 24 hours.' : 'Opened — kept in the Date Night scrapbook.'}
            </Text>
          </>
        ) : /**
         * Wide enough for two columns and the frame stops being the whole screen: the picture
         * takes the larger share and what it costs sits beside it, where it is read while
         * looking at the covers rather than after scrolling past them.
         */
        wide ? (
          <View className="flex-row items-start gap-4">
            <View className="min-w-0 flex-[3]">{frame}</View>
            <View className="min-w-0 flex-[2] gap-2">{meta}</View>
          </View>
        ) : (
          <>
            {frame}
            <View className="gap-2">{meta}</View>
          </>
        )}

        {Platform.OS === 'web' ? (
          <Text variant="note">
            On the web a screenshot cannot be prevented. This is a game between the two of you, not a vault.
          </Text>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Watching one: what they have earned so far, and the two things you can still do about it. */
function SentWrap({
  summary,
  partnerName,
  onChange,
  onFail,
}: {
  summary: WrapSummary;
  partnerName: string;
  onChange: () => Promise<void>;
  onFail: (e: unknown) => void;
}) {
  const [confirmingWithdraw, setConfirmingWithdraw] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await onChange();
    } catch (e) {
      onFail(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent>
        {/* On the band, so the status chip is the band's outline chip rather than a card-side
            colour that would vanish on it. Opened keeps its own weight as a filled one. The word
            is the table's, not the column's — `statusWord` is why a giver no longer reads
            "peeling" about a photograph. */}
        <CardHead row>
          <Text variant="heading" className="min-w-0 flex-1">
            To {partnerName}
          </Text>
          <Badge variant="band" className={summary.status === 'opened' ? 'bg-white/15' : undefined}>
            <Text>{statusWord(summary.status)}</Text>
          </Badge>
        </CardHead>
        {summary.caption ? <Text className="italic">“{summary.caption}”</Text> : null}

        <Row label="Peeled" value={`${summary.peeled} of ${summary.grid}`} />
        <Row label="Price" value={summary.priceLabel} />
        <Row label="Afterwards" value={summary.keep === 'vanish' ? 'vanish after viewing' : 'kept in the scrapbook'} />

        {summary.lastTileWaiting ? (
          <View className="border-gold gap-2 rounded-md border p-3">
            <Text>
              {partnerName} is one tile from opening it.
              {summary.holdUntil ? ` Held until ${whenLabel(summary.holdUntil)}.` : ' You can hold it a day.'}
            </Text>
            {!summary.holdUntil ? (
              <Button
                size="sm"
                variant="outline"
                className="self-start"
                disabled={busy}
                onPress={() => run(() => api.post(`/api/wraps/${summary.id}/hold`))}>
                <Text>Hold a day</Text>
              </Button>
            ) : null}
          </View>
        ) : null}

        {['draft', 'sealed', 'peeling'].includes(summary.status) ? (
          confirmingWithdraw ? (
            <View className="gap-2">
              <Text variant="small" className="text-muted-foreground">
                Withdrawing deletes it. {partnerName} is told nothing — it simply is not there any more.
              </Text>
              <View className="flex-row flex-wrap gap-2">
                <Button
                  variant="destructive"
                  disabled={busy}
                  onPress={() =>
                    run(async () => {
                      await api.post(`/api/wraps/${summary.id}/withdraw`);
                      setConfirmingWithdraw(false);
                    })
                  }>
                  <Text>Withdraw it</Text>
                </Button>
                <Button variant="ghost" flush={false} onPress={() => setConfirmingWithdraw(false)}>
                  <Text>Leave it</Text>
                </Button>
              </View>
            </View>
          ) : (
            <Button variant="outline" size="sm" className="self-start" onPress={() => setConfirmingWithdraw(true)}>
              <Text className="text-destructive">Withdraw</Text>
            </Button>
          )
        ) : null}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View className="border-border flex-row flex-wrap justify-between gap-2 border-b py-1.5">
      <Text variant="small" className="text-muted-foreground">
        {label}
      </Text>
      <Text variant="small">{value}</Text>
    </View>
  );
}
